import { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import api from "../lib/api";
import { ChefHat, Loader2 } from "lucide-react";

/**
 * Extract session_id from multiple URL sources for mobile compatibility.
 * Some mobile browsers (Safari ITP, Chrome Android) may convert hash fragments
 * to query params or lose them entirely during OAuth redirect chains.
 */
function extractSessionId() {
  // 1. Try window.location.hash (most reliable, direct browser API)
  const rawHash = window.location.hash;
  if (rawHash && rawHash.includes("session_id")) {
    try {
      const hashParams = new URLSearchParams(rawHash.substring(1));
      const id = hashParams.get("session_id");
      if (id) return id;
    } catch (e) { /* fallthrough */ }

    // Manual parse fallback (handles URL-encoded hashes)
    const match = rawHash.match(/session_id=([^&]+)/);
    if (match) return decodeURIComponent(match[1]);
  }

  // 2. Try query params (some mobile OAuth flows redirect with ?session_id= instead of #)
  const rawSearch = window.location.search;
  if (rawSearch && rawSearch.includes("session_id")) {
    try {
      const searchParams = new URLSearchParams(rawSearch);
      const id = searchParams.get("session_id");
      if (id) return id;
    } catch (e) { /* fallthrough */ }
  }

  // 3. Try full href as last resort (handles edge cases where fragment/search parsing fails)
  const href = window.location.href;
  const hrefMatch = href.match(/[#?&]session_id=([^&#]+)/);
  if (hrefMatch) return decodeURIComponent(hrefMatch[1]);

  return null;
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const location = useLocation();
  const hasProcessed = useRef(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      try {
        const sessionId = extractSessionId();

        if (!sessionId) {
          console.error("[AuthCallback] No session_id found in URL:", window.location.href);
          toast.error("No se encontro la sesion de autenticacion. Intenta de nuevo.");
          navigate("/login");
          return;
        }

        // Clean the URL immediately to prevent re-processing
        window.history.replaceState(null, "", window.location.pathname);

        // Exchange session_id for app session (with retry for flaky mobile networks)
        let userData = null;
        let lastError = null;

        for (let attempt = 0; attempt < 2; attempt++) {
          try {
            userData = await api.googleAuthSession(sessionId);
            break;
          } catch (err) {
            lastError = err;
            console.warn(`[AuthCallback] Attempt ${attempt + 1} failed:`, err.message);
            if (attempt === 0) {
              await new Promise(r => setTimeout(r, 1000));
            }
          }
        }

        if (!userData) {
          const detail = lastError?.message || "";
          if (detail === "invite_only") {
            toast.error("Acceso restringido: necesitas una invitacion para registrarte.");
          } else if (detail.includes("postMessage") && detail.includes("could not be cloned")) {
            // Browser extension interfering with fetch — surface a clearer message.
            toast.error("Una extensión del navegador está bloqueando el inicio de sesión. Prueba en modo incógnito o desactiva extensiones (Redux/React DevTools, bloqueadores de anuncios) y vuelve a intentarlo.");
          } else if (detail.startsWith("google_auth_failed")) {
            // Backend caught an internal error and tagged it — show the cause
            // and tell the user to retry (often a transient DB/upstream blip).
            toast.error("No pudimos completar el inicio de sesión. Por favor espera unos segundos e inténtalo de nuevo. Si persiste, prueba con email y contraseña.");
            console.error("[AuthCallback] Backend reported google_auth_failed:", detail);
          } else {
            toast.error("Error de autenticacion: " + (detail || "sesion invalida o expirada"));
          }
          navigate("/login");
          return;
        }

        // Cookie is set by backend — navigate without passing state to avoid postMessage clone errors
        toast.success(`Bienvenido, ${userData.name || userData.email}!`);

        // PostHog was intentionally NOT loaded on this page (see
        // public/index.html — the OAuth callback skips the loader because
        // PostHog's async `array.js` patches `fetch`/`XHR` for event
        // captures and breaks the session_id exchange). Now that auth has
        // resolved, inject the official PostHog snippet ourselves so the
        // rest of the SPA session has full analytics. We use the canonical
        // loader URL — the same one the inline snippet would build.
        try {
          if (!window.posthog || window.posthog._i !== undefined) {
            const ph = document.createElement("script");
            ph.async = true;
            ph.crossOrigin = "anonymous";
            ph.src = "https://us-assets.i.posthog.com/static/array.js";
            ph.onload = () => {
              try {
                if (window.posthog && typeof window.posthog.init === "function") {
                  window.posthog.init("phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs", {
                    api_host: "https://us.i.posthog.com",
                    person_profiles: "identified_only",
                    session_recording: {
                      recordCrossOriginIframes: true,
                      capturePerformance: false,
                    },
                  });
                }
              } catch { /* analytics is non-critical */ }
            };
            document.head.appendChild(ph);
          }
        } catch { /* analytics is non-critical */ }

        // Check for pending invitation redirect
        const pendingInvite = sessionStorage.getItem("pending_invite_redirect");
        if (pendingInvite) {
          sessionStorage.removeItem("pending_invite_redirect");
          navigate(pendingInvite);
          return;
        }

        // Check if user has restaurants (separate try-catch)
        try {
          const restaurants = await api.getRestaurants();
          if (restaurants.length === 0) {
            navigate("/onboarding");
          } else {
            navigate("/app");
          }
        } catch {
          // Even if getRestaurants fails, user is authenticated
          navigate("/app");
        }
      } catch (error) {
        console.error("[AuthCallback] Unexpected error:", error);
        toast.error("Error inesperado. Intenta de nuevo.");
        navigate("/login");
      }
    };

    processAuth();
  }, [navigate, location]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-6">
        <div className="w-16 h-16 bg-primary rounded-2xl flex items-center justify-center">
          <ChefHat className="w-9 h-9 text-primary-foreground" />
        </div>
        <div className="flex items-center gap-3">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <p className="text-lg text-muted-foreground">Iniciando sesion...</p>
        </div>
      </div>
    </div>
  );
}
